import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationTokenService } from './authentication-token.service';
import { AuthenticationTokenRefreshService } from './authentication-token-refresh.service';
import { LoginObserverService } from './login-observer.service';
import { IAuthenticationService } from './authentication-service-interface';
import { LastUrlService } from './last-url.service';
import { CurrentUserService } from '../../user/services/current-user.service';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { ToastService, ToastType } from '../toast/toast.service';
import { JsonHttpService } from '../http/json-http.service';
import { AzureADSettings } from '../../azure-ad/models/azure-ad-settings';
import { AzureAdService } from '../../azure-ad/services/azure-ad.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    currentAuthenticationService: IAuthenticationService;

    constructor(
        private router: Router,
        private tokenService: AuthenticationTokenService,
        private tokenRefreshService: AuthenticationTokenRefreshService,
        private loginObserverService: LoginObserverService,
        private lastUrlService: LastUrlService,
        private currentUserService: CurrentUserService,
        private azureAdService: AzureAdService,
        private toastService: ToastService,
        private httpService: JsonHttpService,) {

        loginObserverService.logoutObserver.subscribe((triggeredByUser) => {
            this.logout(triggeredByUser);
        });

        loginObserverService.accessForbiddenObserver.subscribe((error: HttpErrorResponse) => {
            this.navigateToAccessForbidden(error);
        });

        loginObserverService.azureLoginRequiredObserver.subscribe(() => {
            this.navigateToAzureLogin('');
        });

        loginObserverService.azureLoginInsufficientPermissionsObserver.subscribe(() => {
            this.logout(true);
            this.toastService.showToast('Azure AD user does not have sufficient permissions to log into Link', ToastType.error, 5000);
        });
    }


    /**
     * Login with username and password
     * @param username username to be used
     * @param password password to be used
     */
    public async login(username: string, password: string, authCode: string, rememberToken: boolean): Promise<void> {
        await this.tokenRefreshService.login(username, password, authCode, rememberToken);
        this.loginObserverService.userLogIn();
    }

    /**
     * Login with Authorization Code
     * @param authCode code to be used
     */
    public async loginWithAuthCode(authCode: string, rememberToken: boolean, redirectUri: string): Promise<void> {
        await this.tokenRefreshService.loginWithAuthCode(authCode, rememberToken, redirectUri);
        this.loginObserverService.userLogIn();
    }

    /**
     * Request a password reset email for a username.
     */
    public async requestPasswordReset(username: string): Promise<void> {
        const payload = JSON.stringify(username);
        await firstValueFrom(this.httpService.postWithoutAccessToken('/api/login/request-password-reset', payload));
    }

    /**
     * Get Azure AD Settings
     */
    public async getAzureADSettings(): Promise<AzureADSettings> {
        return await this.azureAdService.getAzureADSettings();
    }

    /**
     * Logs out the current user...
     */
    logout(triggeredByUser: boolean) { // Private because we listen for log-out events through the loginObserverService.
        //this.loggingService.logAudit('Authentication_Logout', 'User logged out', { triggeredByUser: triggeredByUser }); // Important to call the log-method before the tokens are removed!

        if (triggeredByUser) {
            this.tokenService.removeRefreshToken(); // Only remove refresh token, if the user actively log out.
            this.currentAuthenticationService.stop(); // Only stop the authentication service, if the user actively log out.
        }
        this.tokenService.removeAccessToken();

        this.navigateToLogin(triggeredByUser);
    }

    /**
     * Navigates to the login-page.
     */
    public navigateToLogin(triggeredByUser = false) {
        if (triggeredByUser) { // If the logout is triggered by a user - no login-redirect should be performed.
            this.lastUrlService.clearLastUrl();
        }
        this.router.navigate(['/login']);
    }

    /**
     * Navigates to the login-page.
     */
    public navigateToAccessForbidden(error: HttpErrorResponse) {
        void this.router.navigate(['/restricted-access'], { replaceUrl: true, state: { error: { status: error.status, statusText: error.statusText, message: error.message, response: error.error } } });
    }

    /**
     * Navigates to Azure AD login.
     */
    public navigateToAzureLogin(state: string) {
        this.tokenService.removeRefreshToken();
        this.currentAuthenticationService.stop();
        this.tokenService.removeAccessToken();

        this.getAzureADSettings().then(settings => {
            if (settings.ClientId) {
                const loginUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize?response_type=code&prompt=select_account&response_mode=fragment&scope=openid%20offline_access%20https://graph.microsoft.com/groupmember.read.all%20https://graph.microsoft.com/user.read';
                const returnUrl = location.origin + '/login';
                const url = loginUrl + '&client_id=' + settings.ClientId + '&redirect_uri=' + returnUrl + '&state=' + state;
                window.location.href = url;
            }
            else {
                this.navigateToLogin(false);
            }
        });
    }

    /**
     * Gets a value indicating whether a user is successfully logged in.
     */
    public isLoggedIn(): Promise<boolean> {
        if (this.currentAuthenticationService) {
            return this.currentAuthenticationService.isAuthenticated();
        }
        else {
            return Promise.resolve(false);
        }
    }

    /**
     * Try to retrive a new access-token. Only call this method if running with token-login.
     */
    public tryGetNewAccessToken() {
        return this.tokenRefreshService.getNewJwt().then(success => {

            if (success) {
                this.loginObserverService.userLogIn();
            }

            return success;
        });
    }

    public async startup(): Promise<void> {
        this.currentAuthenticationService = this.tokenRefreshService;

        if (this.currentAuthenticationService) {
            await this.currentAuthenticationService.startup();

            const isLoggedIn = await this.currentAuthenticationService.isAuthenticated();
            if (isLoggedIn) {
                this.loginObserverService.userLogIn();
            }
        }
    }

    async resetPassword(key: string, password: string): Promise<void> {
        const options = { params: new HttpParams().set('key', key) };
        const payload = JSON.stringify(password);
        await firstValueFrom(this.httpService.postWithoutAccessToken('/api/resetpassword/set-password', payload, options, false));
    }
}
